/**
 * Approval-center kind registry + wire contracts (epic #1030, sales
 * system phase 1).
 *
 * Single source of truth for the approval-kind → approver/requester
 * role maps. E1-S1 (#1037) registers the constants; the generic
 * approval engine (`approval_requests`, E1-S2 #1038) owns the request
 * lifecycle, and the later gift/pricing/credit epics wire each kind to
 * real request flows:
 *
 *   - `customer_transfer`           — wired by E1-S5 (#1041)
 *   - `gift_over_quota`             — wired by E3-G2 (#1103)
 *   - `discount_below_sales_cost`   — wired by E3-P3 (#1106)
 *   - `credit_application`          — wired by E4-C2 (#1126); two-stage
 *                                     dual-sign (lead → finance)
 */

import type { BrandId } from './brands.ts';
import type { CreditBillingTimelineSummary } from './credit-ops-read.ts';
import type { VerifyKeyCacheConvergence } from './public-keys.ts';
import {
  CREDIT_APPLICATION_MAX_LIMIT_CNY_CENTS,
  normalizeCreditApplicationReason,
} from './internal-credit.ts';
import {
  hasAnyOpsRole,
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_SALES_LEAD,
  type OpsPermissionRole,
} from './internal-ops-users.ts';

export const APPROVAL_KINDS = [
  'customer_transfer',
  'gift_over_quota',
  'discount_below_sales_cost',
  'credit_application',
  'temporary_credit_application',
] as const;
export type ApprovalKind = (typeof APPROVAL_KINDS)[number];

/**
 * Roles allowed to decide a request of each kind. Brand scoping
 * (approver must match the request brand, admin bypasses) is enforced
 * by the approval engine, not encoded here.
 */
export const APPROVAL_KIND_APPROVER_ROLES: Readonly<
  Record<ApprovalKind, readonly OpsPermissionRole[]>
> = {
  customer_transfer: [ROLE_OPS_SALES_LEAD],
  gift_over_quota: [ROLE_OPS_SALES_LEAD],
  discount_below_sales_cost: [ROLE_OPS_SALES_LEAD],
  credit_application: [ROLE_OPS_SALES_LEAD, ROLE_OPS_FINANCE_MANAGER],
  temporary_credit_application: [ROLE_OPS_FINANCE_MANAGER],
};

/**
 * Roles allowed to submit a request of each kind. Requester brand is
 * stamped server-side from the requester's sales profile (fail-closed
 * when no profile exists); this map only gates who may open a request.
 * Sales leads inherit `ops-customer-manager` via `expandOpsRoles`, so
 * they can request everything a customer manager can.
 */
export const APPROVAL_KIND_REQUESTER_ROLES: Readonly<
  Record<ApprovalKind, readonly OpsPermissionRole[]>
> = {
  customer_transfer: [ROLE_OPS_CUSTOMER_MANAGER],
  gift_over_quota: [ROLE_OPS_CUSTOMER_MANAGER],
  discount_below_sales_cost: [ROLE_OPS_CUSTOMER_MANAGER],
  credit_application: [ROLE_OPS_CUSTOMER_MANAGER],
  temporary_credit_application: [ROLE_OPS_CUSTOMER_MANAGER],
};

/**
 * Number of sequential approval stages each kind needs (epic #1033
 * E4-C2, #1126). 1 = the classic single-sign lifecycle (every kind
 * except the dual-sign credit application). The platform-api create path
 * stamps `approval_requests.required_stages` from this map, so a kind's
 * stage count is the single source of truth here — no per-request field
 * from the client.
 */
export const APPROVAL_KIND_REQUIRED_STAGES: Readonly<Record<ApprovalKind, number>> = {
  customer_transfer: 1,
  gift_over_quota: 1,
  discount_below_sales_cost: 1,
  credit_application: 2,
  temporary_credit_application: 1,
};

/**
 * Per-stage approver roles for multi-stage kinds (epic #1033 E4-C2,
 * #1126). Index 0 is stage 1, index 1 is stage 2, and so on. Every entry
 * must be a SUBSET of the kind's `APPROVAL_KIND_APPROVER_ROLES` (which is
 * the union used to open the queue). Single-stage kinds have a single
 * entry equal to their approver-role list. `credit_application` splits:
 * stage 1 = same-brand sales lead, stage 2 = finance-manager (admin
 * bypasses both, as everywhere in the engine).
 *
 * The number of entries MUST equal `APPROVAL_KIND_REQUIRED_STAGES[kind]`.
 */
export const APPROVAL_KIND_STAGE_APPROVER_ROLES: Readonly<
  Record<ApprovalKind, readonly (readonly OpsPermissionRole[])[]>
> = {
  customer_transfer: [[ROLE_OPS_SALES_LEAD]],
  gift_over_quota: [[ROLE_OPS_SALES_LEAD]],
  discount_below_sales_cost: [[ROLE_OPS_SALES_LEAD]],
  credit_application: [[ROLE_OPS_SALES_LEAD], [ROLE_OPS_FINANCE_MANAGER]],
  temporary_credit_application: [[ROLE_OPS_FINANCE_MANAGER]],
};

/**
 * The approver roles eligible to decide a request at its CURRENT stage.
 * `currentStage` is 1-based; out-of-range stages fall back to the kind's
 * full approver-role union (defensive — an out-of-range stage should
 * never reach a decide, but never widen silently by returning an empty
 * set that would then be treated as "no one can decide"). Admin bypass
 * is applied by callers, not here.
 */
export function approverRolesForStage(
  kind: ApprovalKind,
  currentStage: number,
): readonly OpsPermissionRole[] {
  const stages = APPROVAL_KIND_STAGE_APPROVER_ROLES[kind];
  const entry = stages[currentStage - 1];
  return entry ?? APPROVAL_KIND_APPROVER_ROLES[kind];
}

export function isApprovalKind(value: unknown): value is ApprovalKind {
  return (
    typeof value === 'string' && (APPROVAL_KINDS as readonly string[]).includes(value)
  );
}

/**
 * Deduplicated union of the per-kind role lists, in kind-registry order.
 * Internal helper for the derived approval-center role sets below.
 */
function approvalRoleUnion(
  kindRoles: Readonly<Record<ApprovalKind, readonly OpsPermissionRole[]>>,
): readonly OpsPermissionRole[] {
  const seen = new Set<OpsPermissionRole>();
  const union: OpsPermissionRole[] = [];
  for (const kind of APPROVAL_KINDS) {
    for (const role of kindRoles[kind]) {
      if (!seen.has(role)) {
        seen.add(role);
        union.push(role);
      }
    }
  }
  return union;
}

/**
 * Roles that can open the approver queue: `ops-admin` (engine-level
 * bypass — decide handlers exempt admins from kind/brand gates) plus
 * every role that can decide at least one registered approval kind.
 *
 * DERIVED from `APPROVAL_KIND_APPROVER_ROLES` (epic #1030 E1-S7,
 * #1043) so the permissions matrix, the ops-console route guards / nav
 * gating / shell chip, and this registry cannot drift: registering a
 * new kind with a new approver role automatically widens the queue
 * surface everywhere.
 */
export const APPROVAL_QUEUE_ROLES: readonly OpsPermissionRole[] = [
  ROLE_OPS_ADMIN,
  ...approvalRoleUnion(APPROVAL_KIND_APPROVER_ROLES),
];

/**
 * Roles that can open the requester "my submissions" view: `ops-admin`
 * plus every role that can submit at least one registered approval
 * kind. Row-level scope (requester = session user) is enforced
 * server-side by ops-api. Derived like `APPROVAL_QUEUE_ROLES`.
 */
export const APPROVAL_MINE_ROLES: readonly OpsPermissionRole[] = [
  ROLE_OPS_ADMIN,
  ...approvalRoleUnion(APPROVAL_KIND_REQUESTER_ROLES),
];

/**
 * Approver roles whose authority spans every brand (#2265).
 *
 * The engine derives an approver's brand from their `sales_profiles`
 * row, which is right for the sales ladder — a sales lead signs for the
 * book they own — and wrong for finance, which is one cross-brand
 * function covering `nadoutong` and `nxtpath` alike. Worse, the profile
 * lookup is fail-closed, so a finance manager with no sales profile (the
 * normal case — finance is not a salesperson) was refused the whole
 * approval center, and the only way to reach both brands was to hold
 * `ops-admin`, which bypasses the stage-role gate as well as the brand
 * one.
 *
 * A role listed here is exempt from the BRAND pin only. It is still
 * subject to the per-stage approver gate (`approverRolesForStage`), to
 * the self-decide ban, and to four-eyes — which is exactly the
 * difference from an admin bypass, and the reason this is a separate set
 * rather than more admins.
 *
 * Declared here so the ops-api gates, platform-api's defense-in-depth
 * re-check and the console guards share one source, like every other
 * derived set in this file.
 */
export const BRAND_AGNOSTIC_APPROVER_ROLES: readonly OpsPermissionRole[] = [
  ROLE_OPS_FINANCE_MANAGER,
];

/**
 * True when `rawRoles` carries a brand-agnostic approver role. Uses
 * `hasAnyOpsRole`, so role expansion applies: `ops-super-admin` expands
 * into the finance role and qualifies, while `ops-admin` does NOT — an
 * admin reaches every brand through the admin bypass, which is tracked
 * separately (see {@link ApprovalActorQualification}).
 */
export function isBrandAgnosticApprover(rawRoles: readonly string[]): boolean {
  return hasAnyOpsRole(rawRoles, BRAND_AGNOSTIC_APPROVER_ROLES);
}

/**
 * How the actor of an approval decision qualified to make it (#2265).
 *
 * `stage_role` — they hold a role listed for the request's current stage
 * in `APPROVAL_KIND_STAGE_APPROVER_ROLES`. This is the intended path,
 * and the only one that makes the dual-sign control mean what it says.
 *
 * `admin_bypass` — they did NOT hold such a role and were let through by
 * the `ops-admin` short-circuit. Substantively still an approval, but
 * not the signature the stage slot claims. Before this was recorded the
 * two were indistinguishable after the fact: `approval.decide` audit
 * metadata named the actor but not the basis, so a stage-2 slot signed
 * by a sales lead through the bypass looked identical to one signed by
 * finance on their own authority.
 */
export const APPROVAL_ACTOR_QUALIFICATIONS = ['stage_role', 'admin_bypass'] as const;
export type ApprovalActorQualification = (typeof APPROVAL_ACTOR_QUALIFICATIONS)[number];

export function isApprovalActorQualification(
  value: unknown,
): value is ApprovalActorQualification {
  return (
    typeof value === 'string' &&
    (APPROVAL_ACTOR_QUALIFICATIONS as readonly string[]).includes(value)
  );
}

// ─── customer_transfer kind payload (E1-S5 #1041) ─────────────────────────

/** `approval_requests.target_type` for `customer_transfer` requests. */
export const CUSTOMER_TRANSFER_TARGET_TYPE = 'org';

export const CUSTOMER_TRANSFER_MODES = ['release', 'transfer'] as const;
export type CustomerTransferMode = (typeof CUSTOMER_TRANSFER_MODES)[number];

export function isCustomerTransferMode(value: unknown): value is CustomerTransferMode {
  return (
    typeof value === 'string' &&
    (CUSTOMER_TRANSFER_MODES as readonly string[]).includes(value)
  );
}

export const CUSTOMER_TRANSFER_REASON_MAX_LENGTH = 500;

/**
 * Kind-specific payload of a `customer_transfer` approval request
 * (epic #1030 E1-S5, #1041):
 *
 *   - `mode: 'release'`  — return the customer to the unowned pool.
 *   - `mode: 'transfer'` — move ownership to `to_ops_user_id`, which must
 *     hold customer-manager authority in the SAME brand as the request
 *     (re-validated at decision time; cross-brand transfers are blocked
 *     by design).
 *   - `reason` — optional requester-facing context shown to the deciding
 *     lead; distinct from the decider's `decision_note`.
 */
export interface CustomerTransferApprovalPayload {
  mode: CustomerTransferMode;
  to_ops_user_id?: string;
  reason?: string;
}

/**
 * Strict shape parser shared by the platform-api create-time validator,
 * the ops-api creation route, and the ops-console dialog. Returns `null`
 * for any payload that is not exactly a valid customer_transfer payload:
 * unknown mode, missing/blank `to_ops_user_id` on transfer, a
 * `to_ops_user_id` on release, unknown extra keys, or an over-long
 * reason.
 */
export function parseCustomerTransferPayload(
  payload: Record<string, unknown>,
): CustomerTransferApprovalPayload | null {
  for (const key of Object.keys(payload)) {
    if (key !== 'mode' && key !== 'to_ops_user_id' && key !== 'reason') {
      return null;
    }
  }
  if (!isCustomerTransferMode(payload.mode)) {
    return null;
  }
  const mode = payload.mode;

  const parsed: CustomerTransferApprovalPayload = { mode };

  const rawTarget = payload.to_ops_user_id;
  if (mode === 'transfer') {
    if (typeof rawTarget !== 'string' || rawTarget.trim().length === 0 || rawTarget.trim().length > 128) {
      return null;
    }
    parsed.to_ops_user_id = rawTarget.trim();
  } else if (rawTarget !== undefined) {
    return null;
  }

  const rawReason = payload.reason;
  if (rawReason !== undefined) {
    if (typeof rawReason !== 'string' || rawReason.length > CUSTOMER_TRANSFER_REASON_MAX_LENGTH) {
      return null;
    }
    const reason = rawReason.trim();
    if (reason.length > 0) {
      parsed.reason = reason;
    }
  }
  return parsed;
}

// ─── gift_over_quota kind payload (E3-G2 #1103) ───────────────────────────

/** `approval_requests.target_type` for `gift_over_quota` requests. */
export const GIFT_OVER_QUOTA_TARGET_TYPE = 'org';

export const GIFT_OVER_QUOTA_REASON_MAX_LENGTH = 500;

/** Whole-yuan CNY cents ceiling shared with the gift-quota config (INT32). */
export const GIFT_OVER_QUOTA_MAX_CNY_CENTS = 2147483647;

/**
 * Kind-specific payload of a `gift_over_quota` approval request
 * (epic #1032 E3-G2, #1103): a CM asked to issue a targeted
 * direct-credit gift whose face value exceeds the org's remaining
 * monthly quota. The approving same-brand sales lead authorizes the
 * excess; on approve the issuance executes INSIDE the decide batch
 * with the configured default expiry — headroom is deliberately NOT
 * re-checked at decision time (the approval exists to exceed it).
 *
 *   - `amount_cny_cents` — requested face value (positive whole-yuan
 *     integer cents).
 *   - `reason` — optional requester-facing context shown to the
 *     deciding lead; distinct from the decider's `decision_note`.
 */
export interface GiftOverQuotaApprovalPayload {
  amount_cny_cents: number;
  reason?: string;
}

/**
 * Strict shape parser shared by the platform-api create-time validator,
 * the ops-api creation gate, and the ops-console gift flow (the
 * `parseCustomerTransferPayload` recipe). Returns `null` for any
 * payload that is not exactly a valid gift_over_quota payload: unknown
 * extra keys, a non-integer / non-positive / non-whole-yuan /
 * INT32-overflowing amount, or an over-long reason.
 */
export function parseGiftOverQuotaPayload(
  payload: Record<string, unknown>,
): GiftOverQuotaApprovalPayload | null {
  for (const key of Object.keys(payload)) {
    if (key !== 'amount_cny_cents' && key !== 'reason') {
      return null;
    }
  }
  const rawAmount = payload.amount_cny_cents;
  if (
    typeof rawAmount !== 'number' ||
    !Number.isFinite(rawAmount) ||
    !Number.isInteger(rawAmount) ||
    rawAmount <= 0 ||
    rawAmount % 100 !== 0 ||
    rawAmount > GIFT_OVER_QUOTA_MAX_CNY_CENTS
  ) {
    return null;
  }

  const parsed: GiftOverQuotaApprovalPayload = { amount_cny_cents: rawAmount };

  const rawReason = payload.reason;
  if (rawReason !== undefined) {
    if (typeof rawReason !== 'string' || rawReason.length > GIFT_OVER_QUOTA_REASON_MAX_LENGTH) {
      return null;
    }
    const reason = rawReason.trim();
    if (reason.length > 0) {
      parsed.reason = reason;
    }
  }
  return parsed;
}

// ─── discount_below_sales_cost kind payload (E3-P3 #1106) ─────────────────

/**
 * `approval_requests.target_type` for `discount_below_sales_cost`
 * requests: the target is one (customer org, SKU) pair, not a whole
 * org — one open discount request per org × SKU.
 */
export const DISCOUNT_BELOW_SALES_COST_TARGET_TYPE = 'org_sku';

export const DISCOUNT_BELOW_SALES_COST_REASON_MAX_LENGTH = 500;

/** Defensive length cap shared by the id/name fields of the payload. */
const DISCOUNT_BELOW_SALES_COST_FIELD_MAX_LENGTH = 256;

/**
 * Composite `target_id` for `discount_below_sales_cost` requests. The
 * separator is safe: org ids (`org_*`) and SKU ids (`sku_*`, migration
 * 0062 surrogates) never contain `:`.
 */
export function discountBelowSalesCostTargetId(orgId: string, skuId: string): string {
  return `${orgId}:${skuId}`;
}

/**
 * Kind-specific payload of a `discount_below_sales_cost` approval
 * request (epic #1032 E3-P3, #1106): a CM's org SKU-rate override write
 * landed below the lead-set sales floor (but at/above the procurement
 * hard floor, checked server-side), so the write converted into a
 * pending approval instead of applying.
 *
 * LEAK GUARD: the requester (CM) sees this payload back through the
 * approvals "mine" view, so it may only ever carry the SALES floor —
 * the procurement cost/floor must never enter this shape.
 *
 *   - `org_id` / `sku_id` — the intercepted write's target; MUST match
 *     the request's composite `target_id`.
 *   - `sku_name` — display name (`namespace_slug/model`) frozen at
 *     submission for queue rendering.
 *   - `proposed_rate_multiplier` — the rate the CM asked for.
 *   - `sales_floor_multiplier` — the sales floor at submission time
 *     (snapshot; decision-time revalidation uses CURRENT floors). The
 *     proposed rate must sit strictly below it — an at/above-floor rate
 *     never needs this approval.
 *   - `reason` — optional requester-facing context (the intercepted
 *     write's note), distinct from the decider's `decision_note`.
 */
export interface DiscountBelowSalesCostApprovalPayload {
  org_id: string;
  sku_id: string;
  sku_name: string;
  proposed_rate_multiplier: number;
  sales_floor_multiplier: number;
  reason?: string;
}

function isBoundedNonEmptyString(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.trim().length <= DISCOUNT_BELOW_SALES_COST_FIELD_MAX_LENGTH
  );
}

/**
 * Strict shape parser shared by the platform-api create-time validator,
 * the ops-api creation gate + write-conversion path, and the
 * ops-console payload summarizer (the `parseCustomerTransferPayload`
 * recipe). Returns `null` for any payload that is not exactly a valid
 * discount_below_sales_cost payload: unknown extra keys, blank ids, a
 * non-finite / non-positive rate or floor, a proposed rate that is not
 * strictly below the floor snapshot, or an over-long reason.
 */
export function parseDiscountBelowSalesCostPayload(
  payload: Record<string, unknown>,
): DiscountBelowSalesCostApprovalPayload | null {
  const allowedKeys = [
    'org_id',
    'sku_id',
    'sku_name',
    'proposed_rate_multiplier',
    'sales_floor_multiplier',
    'reason',
  ];
  for (const key of Object.keys(payload)) {
    if (!allowedKeys.includes(key)) {
      return null;
    }
  }
  if (
    !isBoundedNonEmptyString(payload.org_id) ||
    !isBoundedNonEmptyString(payload.sku_id) ||
    !isBoundedNonEmptyString(payload.sku_name)
  ) {
    return null;
  }
  const proposed = payload.proposed_rate_multiplier;
  const floor = payload.sales_floor_multiplier;
  if (
    typeof proposed !== 'number' ||
    !Number.isFinite(proposed) ||
    proposed <= 0 ||
    typeof floor !== 'number' ||
    !Number.isFinite(floor) ||
    floor <= 0 ||
    proposed >= floor
  ) {
    return null;
  }

  const parsed: DiscountBelowSalesCostApprovalPayload = {
    org_id: payload.org_id.trim(),
    sku_id: payload.sku_id.trim(),
    sku_name: payload.sku_name.trim(),
    proposed_rate_multiplier: proposed,
    sales_floor_multiplier: floor,
  };

  const rawReason = payload.reason;
  if (rawReason !== undefined) {
    if (
      typeof rawReason !== 'string' ||
      rawReason.length > DISCOUNT_BELOW_SALES_COST_REASON_MAX_LENGTH
    ) {
      return null;
    }
    const reason = rawReason.trim();
    if (reason.length > 0) {
      parsed.reason = reason;
    }
  }
  return parsed;
}

// ─── credit_application kind payload (E4-C2 #1126) ────────────────────────

/**
 * `approval_requests.target_type` for `credit_application` requests: the
 * target is one credit application row (`credit_applications.id`), so
 * `uniqueOpenPerTarget` gives exactly one open dual-sign per application.
 */
export const CREDIT_APPLICATION_TARGET_TYPE = 'credit_application';

/** Upper bound on the requested micro-USD limit (JS-safe, matches C1). */
export const CREDIT_APPLICATION_MAX_LIMIT_USD_MICROS = 1_000_000_000_000_000;
/**
 * Inclusive bounds on the requested payment terms (`requested_cycle_days`).
 * "账期 N days" means the statement is due N calendar days after period end
 * (router#1999). No tiers; values outside this range are refused.
 */
export const CREDIT_APPLICATION_MIN_CYCLE_DAYS = 1;
export const CREDIT_APPLICATION_MAX_CYCLE_DAYS = 120;

/**
 * Kind-specific payload of a `credit_application` approval request
 * (epic #1033 E4-C2, #1126; router#1999): every credit application,
 * regardless of amount or payment terms. There is no within-defaults
 * auto-approve path. The dual-sign flow is: stage 1 = same-brand sales
 * lead, stage 2 = finance-manager. The requester cannot be the decider
 * at any stage. On the stage-2 (terminal) approve, the long-term grant
 * is written and `accounts.billing_grace_days` is set to the requested
 * payment terms so statement `due_date` honours the application. The
 * `credit_applications` row flips to `approved` inside the same decide
 * batch. Decision-time revalidation re-checks that the row is still
 * `pending_approval`.
 *
 *   - `application_id` — the `credit_applications.id` created by C1; the
 *     request's `target_id` MUST equal it.
 *   - `org_id` — the customer org (owner-account carrier), frozen at
 *     submission for queue rendering + revalidation.
 *   - `requested_limit_cny_cents` / `requested_cycle_days` — the
 *     application's requested values, frozen for queue rendering. The
 *     decide-time revalidation reads the LIVE application row, not these,
 *     so a tampered payload cannot widen the applied credit. USD is
 *     locked from this CNY amount at approval (router#2036).
 *   - `contract_attachment_key` — the mandatory C1 contract attachment,
 *     surfaced for the deciding lead / finance manager.
 *   - `reason` — required on new submits (router#2077). Absent on
 *     historical payloads filed before the field existed; readers render
 *     that as "未填写".
 */
export interface CreditApplicationApprovalPayload {
  application_id: string;
  org_id: string;
  requested_limit_cny_cents: number;
  requested_cycle_days: number;
  contract_attachment_key: string;
  reason?: string;
}

function isBoundedString(value: unknown, maxLength = 256): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.trim().length <= maxLength
  );
}

/**
 * Strict shape parser shared by the platform-api create-time validator,
 * the ops-api creation path, and the ops-console payload summarizer (the
 * `parseCustomerTransferPayload` recipe). Returns `null` for any payload
 * that is not exactly a valid credit_application payload: unknown extra
 * keys, blank ids, a non-integer / negative / out-of-range limit, a
 * non-positive / out-of-range cycle, or a missing contract key.
 */
export function parseCreditApplicationPayload(
  payload: Record<string, unknown>,
): CreditApplicationApprovalPayload | null {
  const allowedKeys = [
    'application_id',
    'org_id',
    'requested_limit_cny_cents',
    'requested_cycle_days',
    'contract_attachment_key',
    'reason',
  ];
  for (const key of Object.keys(payload)) {
    if (!allowedKeys.includes(key)) {
      return null;
    }
  }
  if (
    !isBoundedString(payload.application_id) ||
    !isBoundedString(payload.org_id) ||
    !isBoundedString(payload.contract_attachment_key)
  ) {
    return null;
  }
  const limit = payload.requested_limit_cny_cents;
  if (
    typeof limit !== 'number' ||
    !Number.isSafeInteger(limit) ||
    limit < 0 ||
    limit > CREDIT_APPLICATION_MAX_LIMIT_CNY_CENTS
  ) {
    return null;
  }
  const cycle = payload.requested_cycle_days;
  if (
    typeof cycle !== 'number' ||
    !Number.isInteger(cycle) ||
    cycle < CREDIT_APPLICATION_MIN_CYCLE_DAYS ||
    cycle > CREDIT_APPLICATION_MAX_CYCLE_DAYS
  ) {
    return null;
  }
  const parsed: CreditApplicationApprovalPayload = {
    application_id: payload.application_id.trim(),
    org_id: payload.org_id.trim(),
    requested_limit_cny_cents: limit,
    requested_cycle_days: cycle,
    contract_attachment_key: payload.contract_attachment_key.trim(),
  };
  if (payload.reason !== undefined) {
    const reason = normalizeCreditApplicationReason(payload.reason);
    if (reason === null) {
      return null;
    }
    parsed.reason = reason;
  }
  return parsed;
}

/**
 * Live customer context attached to credit-kind approval rows at list
 * time (router#2077). Absent on every other kind so the shared queue
 * degrades without extra columns of empty cells in the contract.
 */
export interface CreditApprovalCustomerContext {
  readonly org_id: string;
  readonly org_name: string;
  /** `orgs.created_at` as an ISO instant. */
  readonly registered_at: string;
  /**
   * Live payment-trend summary. Present on the approver queue when the
   * batch read succeeds. Omitted on `/mine` (router#2077 review) and when
   * that org's statements/grants cannot be classified.
   */
  readonly payment_summary?: CreditBillingTimelineSummary;
}

/**
 * Kinds whose requester cannot be the decider, including admin.
 * Long-term and temporary credit both require a distinct human.
 */
export function isRequesterDistinctApprovalKind(kind: ApprovalKind): boolean {
  return kind === 'credit_application' || kind === 'temporary_credit_application';
}

// ─── temporary_credit_application kind payload (router#2000) ──────────────

export interface TemporaryCreditApplicationApprovalPayload {
  application_id: string;
  org_id: string;
  requested_limit_cny_cents: number;
  expected_arrival_amount_cny_cents: number;
  expected_arrival_date: string;
  payment_evidence_text?: string;
  payment_evidence_attachment_key?: string;
}

const TEMPORARY_CREDIT_PAYLOAD_KEYS = [
  'application_id',
  'org_id',
  'requested_limit_cny_cents',
  'expected_arrival_amount_cny_cents',
  'expected_arrival_date',
  'payment_evidence_text',
  'payment_evidence_attachment_key',
] as const;

/**
 * Strict shape parser for a temporary-credit approval payload. Evidence
 * may be text, an attachment key, or both; an empty pair is refused.
 * Extra keys, non-positive amounts, or a non-calendar date refuse.
 */
export function parseTemporaryCreditApplicationPayload(
  payload: Record<string, unknown>,
): TemporaryCreditApplicationApprovalPayload | null {
  for (const key of Object.keys(payload)) {
    let allowed = false;
    for (const candidate of TEMPORARY_CREDIT_PAYLOAD_KEYS) {
      if (candidate === key) {
        allowed = true;
        break;
      }
    }
    if (!allowed) return null;
  }
  if (!isBoundedString(payload.application_id) || !isBoundedString(payload.org_id)) {
    return null;
  }
  const limit = payload.requested_limit_cny_cents;
  const expected = payload.expected_arrival_amount_cny_cents;
  if (
    typeof limit !== 'number' ||
    !Number.isSafeInteger(limit) ||
    limit <= 0 ||
    limit > CREDIT_APPLICATION_MAX_LIMIT_CNY_CENTS
  ) {
    return null;
  }
  if (
    typeof expected !== 'number' ||
    !Number.isSafeInteger(expected) ||
    expected <= 0 ||
    expected > CREDIT_APPLICATION_MAX_LIMIT_CNY_CENTS
  ) {
    return null;
  }
  const arrivalDate = payload.expected_arrival_date;
  if (typeof arrivalDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(arrivalDate)) {
    return null;
  }
  const year = Number(arrivalDate.slice(0, 4));
  const month = Number(arrivalDate.slice(5, 7));
  const day = Number(arrivalDate.slice(8, 10));
  const utc = Date.UTC(year, month - 1, day);
  const dt = new Date(utc);
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() !== month - 1 ||
    dt.getUTCDate() !== day
  ) {
    return null;
  }

  const textRaw = payload.payment_evidence_text;
  const keyRaw = payload.payment_evidence_attachment_key;
  let paymentEvidenceText: string | undefined;
  if (textRaw !== undefined) {
    if (typeof textRaw !== 'string') return null;
    const trimmed = textRaw.trim();
    if (trimmed.length === 0 || trimmed.length > 4_000) return null;
    paymentEvidenceText = trimmed;
  }
  let paymentEvidenceAttachmentKey: string | undefined;
  if (keyRaw !== undefined) {
    if (!isBoundedString(keyRaw, 512)) return null;
    paymentEvidenceAttachmentKey = keyRaw.trim();
  }
  if (paymentEvidenceText === undefined && paymentEvidenceAttachmentKey === undefined) {
    return null;
  }

  return {
    application_id: payload.application_id.trim(),
    org_id: payload.org_id.trim(),
    requested_limit_cny_cents: limit,
    expected_arrival_amount_cny_cents: expected,
    expected_arrival_date: arrivalDate,
    ...(paymentEvidenceText === undefined ? {} : { payment_evidence_text: paymentEvidenceText }),
    ...(paymentEvidenceAttachmentKey === undefined
      ? {}
      : { payment_evidence_attachment_key: paymentEvidenceAttachmentKey }),
  };
}

// ─── lifecycle (E1-S2 #1038) ──────────────────────────────────────────────

export const APPROVAL_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export function isApprovalStatus(value: unknown): value is ApprovalStatus {
  return (
    typeof value === 'string' && (APPROVAL_STATUSES as readonly string[]).includes(value)
  );
}

/** Terminal outcomes an approver can pick. `cancelled` is requester-only. */
export const APPROVAL_DECISIONS = ['approved', 'rejected'] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];

export function isApprovalDecision(value: unknown): value is ApprovalDecision {
  return (
    typeof value === 'string' && (APPROVAL_DECISIONS as readonly string[]).includes(value)
  );
}

// ─── wire shapes ──────────────────────────────────────────────────────────

/**
 * One entry in `approval_requests.stage_decisions` — the append-only log
 * of each stage's approval on a multi-stage request (epic #1033 E4-C2,
 * #1126). Only APPROVE decisions that ADVANCE a stage are recorded here;
 * the terminal decision (final-stage approve, or a rejection at any
 * stage) lands on the request row's `decided_by/decided_at` fields and
 * the in-batch transition row, as for single-stage requests.
 */
export interface ApprovalStageDecision {
  /** The stage number that was approved (1-based). */
  stage: number;
  decided_by_ops_user_id: string;
  decided_at: string;
  /** Always `'approved'` today — only approvals advance a stage. */
  outcome: ApprovalDecision;
  /**
   * How the actor qualified for this stage (#2265). OPTIONAL: entries
   * written before #2265 carry no such field and must keep parsing, so
   * an absent value means "not recorded", never "bypass".
   */
  qualified_by?: ApprovalActorQualification;
}

export function isApprovalStageDecision(value: unknown): value is ApprovalStageDecision {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.stage === 'number' &&
    Number.isInteger(row.stage) &&
    row.stage >= 1 &&
    typeof row.decided_by_ops_user_id === 'string' &&
    typeof row.decided_at === 'string' &&
    isApprovalDecision(row.outcome) &&
    (row.qualified_by === undefined || isApprovalActorQualification(row.qualified_by))
  );
}

/**
 * One row of `approval_requests`. `payload` is the kind-specific body
 * (persisted as `payload_json`, validated per kind at create time).
 *
 * `decided_by_ops_user_id` / `decided_at` / `decision_note` record the
 * terminal actor for every non-pending status — including `cancelled`,
 * where they identify the canceller (requester or admin).
 *
 * Multi-stage fields (epic #1033 E4-C2, #1126): `required_stages` is the
 * total number of sequential approvals a request needs (1 = the classic
 * single-sign lifecycle, default for every existing kind). `current_stage`
 * is the 1-based stage awaiting a decision while `status = 'pending'`.
 * `stage_decisions` logs each intermediate stage's approval; the terminal
 * decision (final-stage approve or any rejection) still lands on the
 * `decided_*` fields.
 */
export interface ApprovalRequestRecord {
  id: string;
  kind: ApprovalKind;
  status: ApprovalStatus;
  brand: BrandId;
  requester_ops_user_id: string;
  target_type: string;
  target_id: string;
  payload: Record<string, unknown>;
  required_stages: number;
  current_stage: number;
  stage_decisions: ApprovalStageDecision[];
  decided_by_ops_user_id: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Live customer identity + payment-trend summary for credit kinds
   * (router#2077). Omitted when the kind is not credit, the payload
   * does not parse, or the org no longer exists.
   */
  credit_customer?: CreditApprovalCustomerContext;
}

/**
 * One row of `approval_request_transitions` — the synchronous,
 * in-batch state history. Written in the SAME atomic batch as the
 * status flip; `ops_audit` stays a best-effort supplement and is never
 * the source of truth for state history.
 */
export interface ApprovalTransitionRecord {
  id: string;
  request_id: string;
  from_status: ApprovalStatus;
  to_status: ApprovalStatus;
  actor_ops_user_id: string;
  note: string | null;
  created_at: string;
}

// ─── /internal/approvals/* contracts ──────────────────────────────────────

/**
 * `POST /internal/approvals`. `brand` is stamped by ops-api from the
 * requester's sales profile — platform-api validates it against the
 * shared brand registry but does not derive it.
 */
export interface CreateApprovalRequestRequest {
  kind: string;
  brand: string;
  requester_ops_user_id: string;
  target_type: string;
  target_id: string;
  payload: Record<string, unknown>;
}

export interface CreateApprovalRequestResponse {
  request: ApprovalRequestRecord;
}

/**
 * `GET /internal/approvals` filters: `status`, `brand`,
 * `requester_ops_user_id`, `kind` (comma-separated), `limit`, `offset`.
 */
export interface ListApprovalRequestsResponse {
  requests: ApprovalRequestRecord[];
  total: number;
  limit: number;
  offset: number;
}

/** `GET /internal/approvals/:id`. */
export interface GetApprovalRequestResponse {
  request: ApprovalRequestRecord;
}

/**
 * `POST /internal/approvals/:id/decide`.
 *
 * The status flip uses a conditional `UPDATE … WHERE status='pending'`
 * gate: exactly one concurrent decide wins; the loser receives
 * `APPROVAL_REQUEST_NOT_PENDING` (409). `expected_brand` and
 * `deny_actor_is_requester` are defense-in-depth re-checks of the
 * ops-api scoping rules (brand match, self-decide ban) — ops-api always
 * sends them for non-admin actors.
 *
 * `expected_stage` (epic #1033 E4-C2, #1126) is the defense-in-depth
 * stage pin for multi-stage kinds: ops-api validates the decider's role
 * against the request's `current_stage` at fetch time and sends that
 * stage here, so a concurrent stage advance between ops-api's role check
 * and the platform decide cannot let a wrong-stage approver act (the
 * decide fails `APPROVAL_REQUEST_NOT_PENDING` when the pinned stage no
 * longer matches). Absent for single-stage kinds / admins.
 *
 * `expected_brand` is absent for two distinct reasons, and platform-api
 * cannot tell them apart — by design, since in both the brand is not the
 * constraint: an admin (bypasses every scope) and a brand-agnostic
 * approver role (#2265, exempt from the brand pin but still gated on the
 * stage role, which `expected_stage` re-checks).
 *
 * `actor_qualified_by` (#2265) records whether the actor held a role for
 * the stage or came through the admin bypass. Provenance only —
 * platform-api stores it and never gates on it, because ops-api has
 * already decided admission and a second opinion computed from an
 * attacker-supplied field would be worthless anyway.
 */
export interface DecideApprovalRequestRequest {
  decision: ApprovalDecision;
  actor_ops_user_id: string;
  note?: string | null;
  expected_brand?: string;
  deny_actor_is_requester?: boolean;
  expected_stage?: number;
  actor_qualified_by?: ApprovalActorQualification;
}

export interface DecideApprovalRequestResponse {
  request: ApprovalRequestRecord;
  /**
   * The in-batch terminal transition row. `null` on a multi-stage
   * ADVANCE (epic #1033 E4-C2, #1126): an intermediate stage approval
   * keeps the request `pending` and writes no `approval_request_transitions`
   * row — the advance is recorded in `request.stage_decisions` instead.
   * Non-null for every terminal decision (final-stage approve, or a
   * rejection at any stage) and every single-stage decide.
   */
  transition: ApprovalTransitionRecord | null;
  /** True when this decide advanced a stage instead of deciding terminally. */
  stage_advanced: boolean;
  /** Present when the terminal decision mutated a verify-key fan-out surface. */
  cache?: VerifyKeyCacheConvergence;
}

/** `POST /internal/approvals/:id/cancel` — same conditional gate as decide. */
export interface CancelApprovalRequestRequest {
  actor_ops_user_id: string;
  note?: string | null;
}

export interface CancelApprovalRequestResponse {
  request: ApprovalRequestRecord;
  transition: ApprovalTransitionRecord;
}

// ─── /v1/ops/approvals contracts (ops-api surface) ────────────────────────

/**
 * `POST /v1/ops/approvals`. Brand is NEVER accepted from the client —
 * ops-api stamps it from the session user's sales profile and fails
 * closed (`SALES_PROFILE_NOT_FOUND`) when the user has none.
 */
export interface OpsCreateApprovalRequest {
  kind: string;
  target_type: string;
  target_id: string;
  payload: Record<string, unknown>;
}

/** `POST /v1/ops/approvals/:id/decide`. Actor is the session user. */
export interface OpsDecideApprovalRequest {
  decision: ApprovalDecision;
  note?: string | null;
}

/** `POST /v1/ops/approvals/:id/cancel`. Actor is the session user. */
export interface OpsCancelApprovalRequest {
  note?: string | null;
}

// ─── kind registry types ──────────────────────────────────────────────────

export type ApprovalPayloadValidationResult =
  | { ok: true }
  | { ok: false; message: string };

/**
 * Kind-specific create-time payload validator. Runs at the platform-api
 * boundary before the pending row is inserted.
 */
export type ApprovalPayloadValidator = (
  payload: Record<string, unknown>,
) => ApprovalPayloadValidationResult;

/**
 * One entry of the approval-kind registry. The engine dispatches
 * payload validation and decide-time side effects by kind.
 *
 * `sideEffect` is generic because hook implementations are runtime
 * concerns (they build SQL statements that join the decide batch) —
 * platform-api instantiates `THook` with its own hook interface. Kinds
 * without a wired flow keep `sideEffect: null`; the engine must treat
 * that as "status flip only" (the `customer_transfer` hook arrives in
 * E1-S5, gift/pricing/credit hooks in Epics 3/4).
 */
export interface ApprovalKindDefinition<THook> {
  readonly kind: ApprovalKind;
  readonly requesterRoles: readonly OpsPermissionRole[];
  readonly approverRoles: readonly OpsPermissionRole[];
  readonly validatePayload: ApprovalPayloadValidator;
  readonly sideEffect: THook | null;
  /**
   * When true, the engine rejects creation with
   * `APPROVAL_DUPLICATE_OPEN_REQUEST` while a pending request of the
   * same kind already exists for the same `(target_type, target_id)` —
   * one in-flight request per target (`customer_transfer` sets this:
   * one open transfer per customer, epic #1030 E1-S5).
   */
  readonly uniqueOpenPerTarget: boolean;
}

export type ApprovalKindRegistry<THook> = ReadonlyMap<
  ApprovalKind,
  ApprovalKindDefinition<THook>
>;
